import { Slider } from "@mui/material";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import styled from "styled-components";
import tinycolor from "tinycolor2";

import { getStaticUrl } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import Logos from "js/core/models/logos";
import { trackActivity } from "js/core/utilities/utilities";
import UploadLogo from "js/editor/ThemeEditor/components/Common/UploadLogo";
import ThemeSection from "js/editor/ThemeEditor/components/ThemeSection";
import { ColorPicker } from "js/react/components/ColorPicker";
import { Gap10, Gap20 } from "js/react/components/Gap";
import Loadable from "js/react/components/Loadable";
import { SimpleLabel } from "js/react/components/SimpleLabel";
import { BlueSwitch } from "js/react/components/Switch";
import { themeColors } from "js/react/sharedStyles";
import { _ } from "js/vendor";
import { FlexBox } from "js/react/components/LayoutGrid";
import { app } from "js/namespaces";

const DEFAULT_LOGO = "/images/beautifulai-logos/beautifulai-logo-reverse.svg";

const defaultBranding = {
    backgroundColor: "#000",
    logoScale: 1,
    logoId: null,
    useLoadingAnimation: true,
    loadingBarColor: "#11A9E2"
};

const AssetContainer = styled.div`
    display: flex;
    width: 100%;
    margin-top: 12px;

    & > div {
        margin-right: 20px;
    }
`;

const AssetScalePicker = styled.div`
    max-width: 245px;
    text-transform: uppercase;
`;

const BackgroundColorPicker = styled.div`
    display: flex;
    align-items: center;
    gap: ${props => props.gap !== undefined ? props.gap : 20}px;
    text-transform: uppercase;
    
    // removes padding from the Color Picker component
    > div {
        padding: 0;
    }
    
    button {
        border: 1px solid ${themeColors.gray};
        width: 30px;
        height: 30px;
    }
`;

const StyledPreview = styled.div`
    padding: 0 30px 30px 30px;
    
    .preview {
        background: black;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 226px;
        max-width: 100%;
        aspect-ratio: 16 / 9;
        border: 1px solid ${themeColors.gray};
    }

    .content {
        display: grid;
        grid-auto-rows: auto;
        gap: 40px;

        > * {
            justify-content: center;
            display: flex;
        }
    }
    
    .logo img {
        max-width: 150px;
        max-height: 150px;
        width: auto;
        height: auto;
    }

    img.default {
        transform: scale(0.6);
    }
`;

const LoadingBarContainer = styled.div`
    display: inline-block;
    
    .animated-loading-bar {
        display: block;
        width: 100px;
        height: 5px;
        overflow: hidden;
    }
    
    @keyframes loadingAnimation {
        0% {
            transform: translateX(-100px);
        }
        100% {
            transform: translateX(100px);
        }
    }
    
    .animated-bar {
        animation: loadingAnimation 2s ease-in-out infinite;
        will-change: transform;
    }
`;

class ThemeLoaderComponent extends Component {
    constructor(props) {
        super(props);

        let playerBranding = this.props.theme.playerBranding || {};

        this.state = {
            backgroundColor: playerBranding.backgroundColor ?? defaultBranding.backgroundColor,
            logoScale: playerBranding.logoScale ?? defaultBranding.logoScale,
            logoId: playerBranding.logoId,
            useLoadingAnimation: playerBranding.useLoadingAnimation ?? defaultBranding.useLoadingAnimation,
            loadingBarColor: playerBranding.loadingBarColor ?? defaultBranding.loadingBarColor
        };

        this.trackingProps = {
            themeOrPresentationId: this.props.themeOrPresentationId
        };
    }

    updateValue = (name, value) => {
        let playerBranding = this.state;
        playerBranding = { ...playerBranding, [name]: value };
        this.setState(playerBranding);
        const includedKeys = ["backgroundColor", "logoScale", "useLoadingAnimation", "loadingBarColor"];
        if (playerBranding.logoId) {
            includedKeys.push("logoId");
        }
        this.props.update({ playerBranding: _.pick(playerBranding, includedKeys) });
    }

    // determines a good background color for a logo
    detectBackgroundColor(rgb) {
        let color = tinycolor(rgb);

        // adjust colors
        if (color.isLight()) {
            color = color.desaturate(50).darken(30);
        } else {
            color = color.desaturate(50).lighten(40);
        }

        return color.toHexString();
    }

    handleSetLogo = async update => {
        let logoSrc;
        if ("logoId" in update) {
            const { logoId } = update;
            this.updateValue("logoId", logoId);

            if (!logoId) {
                this.setState({ logoSrc: null });
                this.updateValue("logoId", null);
                trackActivity("ThemeActions", "RemovedAsset", null, null, this.trackingProps);
            } else {
                // set a new logo
                logoSrc = await Logos.getSignedUrlAndLoad(logoId);
                this.setState({ logoSrc: logoSrc });
                trackActivity("ThemeActions", "UploadedAsset", null, null, this.trackingProps);
            }
        } else if ("averageImageColor" in update) {
            const { averageImageColor } = update;
            const backgroundColor = this.detectBackgroundColor(averageImageColor);
            this.updateValue("backgroundColor", backgroundColor);
        }
    }

    render() {
        const {
            backgroundColor,
            logoScale,
            logoId,
            useLoadingAnimation,
            loadingBarColor,
        } = this.state;

        const { t } = this.props;
        return (
            <ThemeSection title={t("Theme Loader")}>
                <Gap20 />
                <SimpleLabel margin={0}>
                    {t("Asset")}
                </SimpleLabel>

                <AssetContainer>
                    <UploadLogo
                        filePickerOnClick={!logoId}
                        attribute="logoId"
                        src={logoId}
                        update={this.handleSetLogo}
                        allowedFileTypes={["svg+xml", "png", "jpeg", "jpg", "gif"]}
                        disableImageProcessing={file => /\.(svg|gif)$/i.test(file.name)}
                        extractAverageImageColor
                    >
                    </UploadLogo>
                </AssetContainer>
                <Gap20 />

                <BackgroundColorPicker gap={0}>
                    <SimpleLabel>{t("Loading Bar")}</SimpleLabel>
                    <Gap20 />
                    <BlueSwitch
                        checked={useLoadingAnimation}
                        onChange={() => {
                            this.updateValue("useLoadingAnimation", !useLoadingAnimation);
                            trackActivity("ThemeActions", "EditedLoaderBar", null, null, this.trackingProps);
                        }}
                    />
                    {useLoadingAnimation && (
                        <ColorPicker
                            disableAlpha
                            value={loadingBarColor}
                            showColorPicker
                            onOpenPicker={() => this.setState({ barColorOnOpen: this.state.loadingBarColor })}
                            onClosePicker={() => {
                                if (this.state.barColorOnOpen != this.state.loadingBarColor) {
                                    trackActivity("ThemeActions", "ChangedLoadingBarColor", null, null, this.trackingProps);
                                    this.setState({ barColorOnOpen: null });
                                }
                            }}
                            onChange={value => {
                                if (app.currentTheme && app.currentTheme.palette) {
                                    const paletteColors = app.currentTheme.palette.getBackgroundColors();
                                    if (paletteColors[value]) {
                                        value = paletteColors[value];
                                    }
                                }
                                this.updateValue("loadingBarColor", value);
                            }}
                        >

                            <SimpleLabel>{t("Theme Colors")}</SimpleLabel>

                        </ColorPicker>
                    )}
                </BackgroundColorPicker>
                <Gap20 />
                <AssetScalePicker>
                    <SimpleLabel tooltip={t("Adjusts the scale of the asset.")}>
                        {t("Asset Scale")}
                    </SimpleLabel>
                    <Slider
                        value={logoScale}
                        onChange={(event, scale) => {
                            if (!this.state.scaleOnOpen) {
                                this.setState({ scaleOnOpen: this.state.logoScale });
                            }
                            this.updateValue("logoScale", scale);
                        }}
                        onChangeCommitted={(event, scale) => {
                            this.updateValue("logoScale", scale);
                            if (this.state.scaleOnOpen != scale) {
                                trackActivity("ThemeActions", "EditedScaleAsset", null, null, this.trackingProps);
                                this.setState({ scaleOnOpen: null });
                            }
                        }}
                        min={0.8}
                        max={1.5}
                        step={0.05}
                    />
                </AssetScalePicker>
                <Gap20 />
                <BackgroundColorPicker>
                    <SimpleLabel>{t("Background Color")}</SimpleLabel>
                    <ColorPicker
                        disableAlpha
                        value={backgroundColor}
                        showColorPicker
                        onOpenPicker={() => this.setState({ colorOnOpen: this.state.backgroundColor })}
                        onClosePicker={() => {
                            if (this.state.colorOnOpen != this.state.backgroundColor) {
                                trackActivity("ThemeActions", "ChangedBackgroundColor", null, null, this.trackingProps);
                                this.setState({ colorOnOpen: null });
                            }
                        }}
                        onChange={value => {
                            if (app.currentTheme && app.currentTheme.palette) {
                                const paletteColors = app.currentTheme.palette.getBackgroundColors();
                                if (paletteColors[value]) {
                                    value = paletteColors[value];
                                }
                            }
                            this.updateValue("backgroundColor", value);
                        }}
                    >
                        <SimpleLabel>{t("Theme Colors")}</SimpleLabel>
                    </ColorPicker>
                </BackgroundColorPicker>
            </ThemeSection>
        );
    }
}

class ThemeLoaderPreviewComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            logoSrc: null,
            isLoading: true
        };
    }

    componentDidMount() {
        const logoId = this.props.theme.playerBranding?.logoId;
        this.loadLogoSrc(logoId);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.theme.playerBranding?.logoId != this.props.theme.playerBranding?.logoId) {
            this.loadLogoSrc(this.props.theme.playerBranding?.logoId);
        }
    }

    loadLogoSrc = async logoId => {
        const logoSrc = logoId
            ? await Logos.getSignedUrlAndLoad(logoId)
            : getStaticUrl(DEFAULT_LOGO);

        this.setState({ logoSrc, isLoading: false });
    }

    render() {
        let { theme } = this.props;
        let playerBranding = {
            backgroundColor: theme.playerBranding?.backgroundColor ?? defaultBranding.backgroundColor,
            logoId: theme.playerBranding?.logoId ?? defaultBranding.logoId,
            logoScale: theme.playerBranding?.logoScale ?? defaultBranding.logoScale,
            useLoadingAnimation: theme.playerBranding?.useLoadingAnimation ?? defaultBranding.useLoadingAnimation,
            loadingBarColor: theme.playerBranding?.loadingBarColor ?? defaultBranding.loadingBarColor,
        };

        const {
            backgroundColor,
            logoScale,
            useLoadingAnimation,
            loadingBarColor
        } = playerBranding;
        const {
            isLoading,
            logoSrc
        } = this.state;
        const logoAltText = WorkspaceController.workspace?.name ?? "Beautiful.ai";
        const logoStyle = { transform: `scale(${logoScale ?? 1})` };
        const previewStyle = { backgroundColor: (backgroundColor ?? "#000") };
        const { t } = this.props;
        return (
            <StyledPreview>
                <Gap20 />
                <SimpleLabel margin={0}>
                    {t("Preview")}
                </SimpleLabel>
                <Gap10 />
                <Loadable isLoading={isLoading}>
                    <div className="preview" style={previewStyle}>
                        <div className="content">
                            <div className="logo" style={logoStyle}>
                                <img src={logoSrc} alt={logoAltText} />
                            </div>
                            {useLoadingAnimation && (
                                <LoadingBarContainer>
                                    <svg className="animated-loading-bar" viewBox="0 0 100 5" fill="none">
                                        <rect x="0" y="0" width="100" height="5" rx="4" fill="white" stroke="white" strokeWidth="1"/>
                                        <rect className="animated-bar" x="1" y="1" width="48" height="3" rx="1.5" fill={loadingBarColor}/>
                                    </svg>
                                </LoadingBarContainer>
                            )}
                        </div>
                    </div>
                </Loadable>
            </StyledPreview>
        );
    }
}

export const ThemeLoader = withTranslation()(ThemeLoaderComponent);
export const ThemeLoaderPreview = withTranslation()(ThemeLoaderPreviewComponent);
